import { PaymentModalityType } from '@/common/types/companies/companies.type'
import { formatCurrency, truncateText } from '@/common/utils/formatters'
import { GovernmentAllocationData } from '@/features/governmentAllocations/types/governmentAllocation.type'
import { BoxesIcon, FileBoxIcon } from 'lucide-react'
import { useId } from 'react'
import { Tooltip } from 'react-tooltip'

type GovernmentAllocationDetailsProps = {
  governmentAllocation: GovernmentAllocationData
  paymentModality?: PaymentModalityType
}

export const GovernmentAllocationDetails = ({
  governmentAllocation,
  paymentModality,
}: GovernmentAllocationDetailsProps) => {
  const tooltipId = useId()

  const entryProductsCategories = governmentAllocation.entry_product_categories_without_products || [];
  const entryProducts = governmentAllocation.entry_products || [];


  return (
    <div className="flex gap-4 items-end w-full">
      <div className="flex flex-col gap-2 w-full">
        <div className='grow flex items-center justify-between gap-2'>
          <>
            <p className="text-xs font-bold leading-3 cursor-default" data-tooltip-id={`${tooltipId}_display_name_tooltip`}>
              {governmentAllocation.main_entry.office_number} - {truncateText(governmentAllocation.branch_display_name, 30)}
            </p>

            <Tooltip id={`${tooltipId}_display_name_tooltip`} className="z-50">
              {governmentAllocation.branch_display_name}
            </Tooltip>
          </>

          {[...entryProductsCategories, ...entryProducts].length > 0 && (
            <>
              <div className='flex items-center gap-1' data-tooltip-id={`${tooltipId}_entry_products_tooltip`}>
                {entryProductsCategories.length > 0 ? (
                  <>
                    <BoxesIcon strokeWidth={1.5} className='size-5' />

                    <p className="text-xs leading-3 cursor-default italic">
                      {governmentAllocation.entry_product_categories_without_products[0].product_category.display_name}
                    </p>
                  </>
                ) : (
                  <>
                    <FileBoxIcon strokeWidth={1.5} className='size-5' />

                    <p className="text-xs leading-3 cursor-default italic">
                      {governmentAllocation.entry_products[0].product.display_name}
                    </p>
                  </>
                )}

                {[...entryProductsCategories, ...entryProducts].length > 1 && (
                  <div className='bg-primary-blue px-2 py-1 rounded-full ml-1'>
                    <p className="text-xs font-bold leading-3 cursor-default">
                      + {[...entryProductsCategories, ...entryProducts].length - 1}
                    </p>
                  </div>
                )}
              </div>

              <Tooltip id={`${tooltipId}_entry_products_tooltip`} className="z-50">
                <div className='flex flex-col gap-2'>
                  {entryProductsCategories.length > 0 && (
                    <div>
                      <div className='flex items-center gap-1'>
                        <BoxesIcon strokeWidth={1.5} className='size-4 text-white-400' />

                        <p className="text-xs leading-3 italic text-white-400">
                          Categorias
                        </p>
                      </div>

                      <p>
                        {entryProductsCategories.map(({ product_category }) => product_category.display_name).join(', ')}
                      </p>
                    </div>
                  )}

                  {entryProducts.length > 0 && (
                    <div>
                      <div className='flex items-center gap-1'>
                        <FileBoxIcon strokeWidth={1.5} className='size-4 text-white-400' />

                        <p className="text-xs leading-3 italic text-white-400">
                          Produtos
                        </p>
                      </div>

                      <p>
                        {entryProducts.map(({ product }) => product.display_name).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </Tooltip>
            </>
          )}
        </div>

        <div className="flex flex-row gap-2">
          <div className="flex flex-col gap-2">
            <div className='flex flex-col gap-2'>
              <p className="text-[10px] font-light italic leading-3">
                {paymentModality === 'pre_paid' ? 'Empenhado' : 'Cotado'}
              </p>
              <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.total_value))}</p>
            </div>

            <div className="text-yellow-400 flex flex-col gap-2">
              <p className="text-[10px] font-light italic leading-3">Consumido</p>
              <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.consumed_total))}</p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className='flex flex-col gap-2'>
              <p className="text-[10px] font-light italic leading-3">Aprovado</p>
              <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.approved_value))}</p>
            </div>

            <div className="text-valid flex flex-col gap-2">
              <p className="text-[10px] font-light italic leading-3">Disponível</p>
              <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.remaining_total))}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
